import React, { useState } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { useSnackbar } from 'notistack'
import { extractErrorMessage } from '../../../utils/extractErrorMessage'
import { LIST_MAX_WIDTH } from '../draw'
import {
  useDeleteLuckyDrawPrizeWinners,
  useDeleteLuckyDrawWinner,
} from '../../../api/lucky-draw'

/** The operator's record of who has won this prize. Nothing to show until the first award. */
const WinnersBoard = ({ drawId, prize, winners, onRemoved }) => {
  // null, { winner } for one row, or { all: true } for the whole prize.
  const [confirming, setConfirming] = useState(null)
  const { mutateAsync: removeWinner, isLoading: isRemovingOne } = useDeleteLuckyDrawWinner(drawId)
  const { mutateAsync: removeAll, isLoading: isRemovingAll } =
    useDeleteLuckyDrawPrizeWinners(drawId)
  const { enqueueSnackbar } = useSnackbar()

  const isRemoving = isRemovingOne || isRemovingAll

  // No success toast — the rows disappearing is the confirmation. Failures still surface.
  const handleConfirm = async () => {
    const removedIds = confirming.all
      ? winners.map((winner) => winner.id)
      : [confirming.winner.id]

    try {
      if (confirming.all) await removeAll(prize.id)
      else await removeWinner(confirming.winner.id)

      setConfirming(null)
      onRemoved(removedIds)
    } catch (e) {
      enqueueSnackbar(extractErrorMessage(e), { variant: 'error' })
    }
  }

  if (winners.length === 0) return null

  return (
    <Box
      pb={4}
      px={{ xs: 2, md: 4 }}
      width='100%'
      // No global box-sizing reset, so the padding would otherwise widen this past the page.
      sx={{ maxWidth: LIST_MAX_WIDTH, mx: 'auto', boxSizing: 'border-box' }}
    >
      {/* Heading and its one action share a row — the board owns both. */}
      <Box display='flex' justifyContent='space-between' alignItems='center' mb={1}>
        <Typography sx={{ color: 'rgba(255, 255, 255, 0.55)', letterSpacing: '0.3em', fontSize: '0.75rem' }}>
          WINNERS
        </Typography>
        <Button
          size='small'
          startIcon={<CloseIcon />}
          onClick={() => setConfirming({ all: true })}
          sx={{ color: 'rgba(255, 255, 255, 0.55)', '&:hover': { color: '#ff6b6b' } }}
        >
          REMOVE ALL
        </Button>
      </Box>

      <Box display='flex' flexDirection='column'>
        {winners.map((winner, index) => (
          <Box
            key={winner.id}
            display='flex'
            alignItems='baseline'
            flexWrap='wrap'
            gap={{ xs: 1, md: 2 }}
            py={1.25}
            px={1}
            sx={{
              // Rules between rows only — a trailing one reads as a stray line under the list.
              borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
              '&:last-of-type': { borderBottom: 'none' },
              // Newest first, so the top row is the one just announced.
              backgroundColor: index === 0 ? 'rgba(255, 255, 255, 0.05)' : 'transparent',
            }}
          >
            {/* Just the name: the list is already filtered to one prize, and the stage header
                above says which. */}
            <Typography
              sx={{
                color: '#fff',
                fontWeight: 700,
                textTransform: 'uppercase',
                fontSize: 'clamp(1rem, 1.4vw, 1.35rem)',
                lineHeight: 1.2,
                overflowWrap: 'anywhere',
                flex: 1,
              }}
            >
              {winner.companyName}
            </Typography>
            <Tooltip title='Remove this winner'>
              <IconButton
                size='small'
                onClick={() => setConfirming({ winner })}
                sx={{ color: 'rgba(255,255,255,0.25)', '&:hover': { color: '#ff6b6b' } }}
              >
                <CloseIcon fontSize='small' />
              </IconButton>
            </Tooltip>
          </Box>
        ))}
      </Box>

      <Dialog maxWidth='sm' open={Boolean(confirming)}>
        <DialogTitle>{confirming?.all ? 'Remove All Winners' : 'Remove Winner'}</DialogTitle>
        <DialogContent dividers>
          {confirming?.all ? (
            <Typography>
              Remove all <b>{winners.length}</b> winner{winners.length === 1 ? '' : 's'} of{' '}
              <b>{prize?.name}</b>? Every unit goes back into the pool and those companies can win
              again.
            </Typography>
          ) : (
            <Typography>
              Remove <b>{confirming?.winner.companyName}</b> as a winner of{' '}
              <b>{confirming?.winner.prizeName}</b>? The prize goes back into the pool and the
              company can win again.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant='outlined' onClick={() => setConfirming(null)} disabled={isRemoving}>
            Cancel
          </Button>
          <Button variant='contained' color='error' onClick={handleConfirm} disabled={isRemoving}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default WinnersBoard
